import {
  DrawerCategoryOption,
  TextStyle,
  useAssemblyNavigate,
  useDeviceInfo,
} from '@assembly-web/ui';
import {
  BookOpenIcon,
  PlusIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { defineMessages, useIntl } from 'react-intl';
import { twMerge } from 'tailwind-merge';

import { useMultiDrawerStore } from '../../../../../stores/useMultiDrawerStore';
import { useUserDetails } from '../../../hooks/useUserDetails';

const messages = defineMessages({
  header: {
    defaultMessage: 'How do you want to create a new flow?',
    id: 'fe02JA',
  },
  subHeader: {
    defaultMessage:
      'Get assistance, browse our pre-built templates, or start fresh',
    id: 'SOU+iF',
  },
  askDoraTitle: {
    defaultMessage: 'Just ask DoraAI',
    id: 'boDsaI',
  },
  askDoraDesc: {
    defaultMessage:
      'Tell DoraAI what you want to build, and get a workflow form tailored to your needs!',
    id: 'J3rjkn',
  },
  templateLibraryTitle: {
    defaultMessage: 'Template library',
    id: 'lO/Ima',
  },
  templateLibraryDesc: {
    defaultMessage:
      "Explore 200+ template suggestions created by Assembly's team.",
    id: 'ormEyh',
  },
  fromScratchTitle: {
    defaultMessage: 'From scratch',
    id: 'CYmj9p',
  },
  fromScratchDesc: {
    defaultMessage: 'A fully manual workflow building experience.',
    id: 'shBQ1S',
  },
  getStarted: {
    defaultMessage: 'Get started',
    id: '/aBLH2',
  },
});

function AskDoraOption({
  isMobile,
  onClick,
}: {
  isMobile: boolean;
  onClick: () => void;
}) {
  const { formatMessage } = useIntl();

  return (
    <DrawerCategoryOption.Root
      className={twMerge(
        'w-full border-purple-7 bg-gradient-to-r hover:from-[#1d3fc8]/10 hover:via-purple-7/10 hover:to-[#a309ca]/10 focus:from-[#1d3fc8]/20 focus:via-purple-7/20 focus:to-[#a309ca]/20',
        !isMobile && 'px-8 py-[30px]'
      )}
      cta={
        <DrawerCategoryOption.CTA>
          {formatMessage(messages.getStarted)}
        </DrawerCategoryOption.CTA>
      }
      description={formatMessage(messages.askDoraDesc)}
      icon={
        <DrawerCategoryOption.Icon className="bg-gradient-to-r from-[#1d3fc8] via-purple-7 to-[#a309ca]">
          <SparklesIcon className="h-5 w-5 stroke-gray-1 stroke-2" />
        </DrawerCategoryOption.Icon>
      }
      onClick={onClick}
      title={
        <DrawerCategoryOption.Title variant="lg-bold" className="mb-1">
          {formatMessage(messages.askDoraTitle)}
        </DrawerCategoryOption.Title>
      }
    />
  );
}

function TemplateLibraryOption({
  isMobile,
  onClick,
}: {
  isMobile: boolean;
  onClick: () => void;
}) {
  const { formatMessage } = useIntl();

  return (
    <DrawerCategoryOption.Root
      className={twMerge(
        'border-gray-5 hover:bg-gray-3 focus:bg-gray-3',
        isMobile ? 'w-full' : 'w-1/2'
      )}
      cta={
        <DrawerCategoryOption.CTA>
          {formatMessage(messages.getStarted)}
        </DrawerCategoryOption.CTA>
      }
      description={formatMessage(messages.templateLibraryDesc)}
      icon={
        <DrawerCategoryOption.Icon className="bg-gray-9">
          <BookOpenIcon className="h-5 w-5 stroke-gray-1 stroke-2" />
        </DrawerCategoryOption.Icon>
      }
      onClick={onClick}
      title={
        <DrawerCategoryOption.Title variant="lg-bold">
          {formatMessage(messages.templateLibraryTitle)}
        </DrawerCategoryOption.Title>
      }
    />
  );
}

function FromScratchOption({
  isMobile,
  onClick,
}: {
  isMobile: boolean;
  onClick: () => void;
}) {
  const { formatMessage } = useIntl();

  return (
    <DrawerCategoryOption.Root
      className={twMerge(
        'border-gray-5 hover:bg-gray-3 focus:bg-gray-3',
        isMobile ? 'w-full' : 'w-1/2'
      )}
      cta={
        <DrawerCategoryOption.CTA>
          {formatMessage(messages.getStarted)}
        </DrawerCategoryOption.CTA>
      }
      description={formatMessage(messages.fromScratchDesc)}
      icon={
        <DrawerCategoryOption.Icon className="bg-gray-9">
          <PlusIcon className="h-5 w-5 stroke-gray-1 stroke-2" />
        </DrawerCategoryOption.Icon>
      }
      onClick={onClick}
      title={
        <DrawerCategoryOption.Title variant="lg-bold">
          {formatMessage(messages.fromScratchTitle)}
        </DrawerCategoryOption.Title>
      }
    />
  );
}

export function FlowCreationEmptyView() {
  const { formatMessage } = useIntl();
  const { deviceType } = useDeviceInfo();
  const isMobile = deviceType === 'mobile';

  const [{ data: userDetails }] = useUserDetails();
  const workspaceSlugPath = userDetails?.assembly.workspaceSlugPath;
  const navigate = useAssemblyNavigate();
  const { deleteDrawer, deleteOverflowDrawer, findAndUpdateDrawerField } =
    useMultiDrawerStore();

  const onAskDoraClick = () => {
    findAndUpdateDrawerField('flowCreation', (draft) => {
      draft.data = { view: 'chat' };
    });
  };

  const onTemplateLibraryClick = () => {
    navigate('/templates');
    deleteDrawer('flowCreation');
    deleteOverflowDrawer('flowCreation');
  };

  const onFromScratchClick = () => {
    navigate(`/${workspaceSlugPath}/flows/editor`);
    deleteDrawer('flowCreation');
    deleteOverflowDrawer('flowCreation');
  };

  return (
    <div className="flex h-full flex-col bg-gray-2 px-10 py-6">
      <TextStyle variant="lg-bold">{formatMessage(messages.header)}</TextStyle>
      <TextStyle variant="base-regular" className="text-gray-8">
        {formatMessage(messages.subHeader)}
      </TextStyle>
      <div className="mt-4 flex flex-col gap-3 pb-8">
        {!isMobile ? (
          <>
            <AskDoraOption isMobile={isMobile} onClick={onAskDoraClick} />
            <div className="flex gap-3">
              <TemplateLibraryOption
                isMobile={isMobile}
                onClick={onTemplateLibraryClick}
              />
              <FromScratchOption
                isMobile={isMobile}
                onClick={onFromScratchClick}
              />
            </div>
          </>
        ) : (
          <>
            <AskDoraOption isMobile={isMobile} onClick={onAskDoraClick} />
            <TemplateLibraryOption
              isMobile={isMobile}
              onClick={onTemplateLibraryClick}
            />
            <FromScratchOption
              isMobile={isMobile}
              onClick={onFromScratchClick}
            />
          </>
        )}
      </div>
    </div>
  );
}
